import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext, DragEndEvent, DragStartEvent, KeyboardSensor, PointerSensor, TouchSensor, closestCenter,
  useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFollowAnchor } from '../../hooks/useFollowAnchor';
import { Z } from '../../styles/zLayers';
import { PexelsMediaPicker } from '../tend/PexelsMediaPicker';
import { PexelsMediaItem } from '../../api/pexelsApi';
import MediaTile, { TileFit } from './MediaTile';

/**
 * The Content Media Gallery — opened from the Media Control's Preview.
 * Primarily a selection tool: one click selects and closes. Hold and drag
 * reorders (numbers update live, the order is saved on drop). Each tile
 * carries the card's delete; Plus pops the shared Pexels / Upload picker
 * down. The caller (the card's adapter) owns every meaning: what select,
 * reorder, delete and add do for an original vs a tended post.
 *
 * The shell is a FLOATING PANEL, not a modal: on a wide viewport it sits
 * to the right of the card (left when there is no room), sized to the room
 * there is, following the card as the page scrolls — the post and its
 * media stay visible together; no veil, no blur. On a narrow viewport it
 * is a bottom sheet. Surface and header = the rendered preview panel
 * (`.tend-surface .tend-f2`, Inter Tight title, round icon buttons).
 */
export interface MediaGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The card (or its media box) the panel sits beside. */
  anchorEl?: HTMLElement | null;
  title?: string;
  subtitle?: string;
  pool: string[];
  selectedUrl?: string | null;
  heroUrl?: string | null;
  resolveUrl?: (url: string) => string | undefined;
  onSelect: (url: string) => void;
  onReorder: (urls: string[]) => void;
  onDelete?: (url: string) => void;
  deleteTitle?: string;
  onAddPexels?: (media: PexelsMediaItem) => void;
  onAddUpload?: () => void;
  isUploading?: boolean;
  allowedType?: 'photos' | 'videos' | 'both';
  zIndex?: number;
  /** Test/instance id for the picker. */
  instanceId?: string;
  /** The calling card's media aspect ('9/16') and fit/fill — tiles show what selecting would look like. */
  tileAspect?: string;
  tileFit?: TileFit;
}

const PANEL_MAX_WIDTH = 600;
const PANEL_MIN_WIDTH = 320;
const GAP = 16;
const PAD = 18;
const WIDE_QUERY = '(min-width: 1024px)';

/** True when an ancestor sits in the item-modal band (FeedbackPanel's rule). */
export const isInsideModalBand = (el: HTMLElement | null | undefined): boolean => {
  let node: HTMLElement | null = el ?? null;
  while (node && node !== document.body) {
    const z = parseInt(window.getComputedStyle(node).zIndex, 10);
    if (!Number.isNaN(z) && z >= Z.ITEM_MODAL) return true;
    node = node.parentElement;
  }
  return false;
};

/** The panel's rung: above the item modal when the card is inside it, else above the card. */
export const galleryZIndex = (anchorEl: HTMLElement | null | undefined): number =>
  (isInsideModalBand(anchorEl) ? Z.FEEDBACK : Z.ABOVE_ITEM);

const useWide = (): boolean => {
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(WIDE_QUERY).matches : true));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
};

interface PanelPos { left: number; top: number; width: number; height: number }

const MIN_HEIGHT = 220;

/** Beside the card: right of it with a gap, left when there is no room there,
 *  as wide as the room allows (capped). Vertically it is ANCHORED TO THE CARD
 *  like the feedback panel: it spans from the card's top to its bottom,
 *  clipped to the viewport (and to the sticky headers via `followTop`), so it
 *  slides along a tall card as the page scrolls. Pure, so it is testable. */
export const placeBeside = (anchor: DOMRect, viewportW: number, viewportH: number, followTop: number = GAP): PanelPos => {
  const top = Math.max(followTop, anchor.top);
  const bottom = Math.min(anchor.bottom, viewportH - GAP);
  const height = Math.max(MIN_HEIGHT, bottom - top);
  const roomRight = viewportW - anchor.right - 2 * GAP;
  const roomLeft = anchor.left - 2 * GAP;
  let width: number;
  let left: number;
  if (roomRight >= PANEL_MIN_WIDTH || roomRight >= roomLeft) {
    width = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, roomRight));
    left = Math.min(anchor.right + GAP, viewportW - width - GAP);
  } else {
    width = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, roomLeft));
    left = Math.max(GAP, anchor.left - GAP - width);
  }
  return { left: Math.max(GAP, left), top, width, height };
};

/** The bottom of the page's sticky headers (the feedback panel's rule), so the
 *  panel's top never slides under them. */
const stickyHeaderBottom = (): number => {
  let bottom = 0;
  document.querySelectorAll('.tend-page-header, .tend-pubtabs-sticky').forEach((el) => {
    bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
  });
  return bottom;
};

/** Pure: the list after moving `from` to `to` (dnd-kit's arrayMove). */
export const reorderList = (urls: string[], from: number, to: number): string[] => arrayMove(urls, from, to);

const SortableTile: React.FC<{
  url: string; number: number; selected: boolean; hero: boolean; resolveUrl?: (u: string) => string | undefined;
  onSelect: () => void; onDelete?: () => void; deleteTitle?: string; aspect?: string; fit?: TileFit;
}> = ({ url, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  return (
    <MediaTile
      url={url}
      {...rest}
      tileRef={setNodeRef}
      dragging={isDragging}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      dragProps={{ ...attributes, ...listeners }}
    />
  );
};

export const MediaGalleryModal: React.FC<MediaGalleryModalProps> = ({
  isOpen, onClose, anchorEl, title = 'Media', subtitle, pool, selectedUrl, heroUrl, resolveUrl,
  onSelect, onReorder, onDelete, deleteTitle = 'Delete', onAddPexels, onAddUpload, isUploading = false,
  allowedType = 'both', zIndex, instanceId = 'gallery', tileAspect, tileFit,
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const justDragged = useRef(false);

  // Where it sits: beside the card on a wide viewport (following it), a
  // bottom sheet otherwise.
  const wide = useWide();
  const anchorRef = useRef<HTMLElement | null>(anchorEl ?? null);
  anchorRef.current = anchorEl ?? null;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) {
      const vw = window.innerWidth; const vh = window.innerHeight;
      const width = Math.min(PANEL_MAX_WIDTH, vw - 2 * GAP);
      setPos({ left: Math.max(GAP, vw - width - GAP), top: Math.round(vh * 0.08), width, height: Math.round(vh * 0.85) });
      return;
    }
    const followTop = Math.max(GAP, stickyHeaderBottom() + 12);
    setPos(placeBeside(el.getBoundingClientRect(), window.innerWidth, window.innerHeight, followTop));
  }, []);
  useEffect(() => { if (isOpen && wide) place(); }, [isOpen, wide, place, anchorEl]);
  useFollowAnchor(isOpen && wide && !!anchorEl, anchorRef, place, onClose, panelRef);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  const panelZ = galleryZIndex(anchorEl);

  // Hold to drag: a plain click selects; a press that moves after a short
  // delay reorders. Keyboard: space/enter on a focused tile + arrows.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!isOpen) setShowAdd(false);
  }, [isOpen]);

  const handleDragStart = useCallback((_e: DragStartEvent) => { justDragged.current = true; }, []);
  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;
    window.setTimeout(() => { justDragged.current = false; }, 0);
    if (!over || active.id === over.id) return;
    const from = pool.indexOf(String(active.id));
    const to = pool.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(reorderList(pool, from, to));
  }, [pool, onReorder]);

  const select = useCallback((url: string) => {
    if (justDragged.current) return;          // the click that ends a drag is not a pick
    if (url !== selectedUrl) onSelect(url);
    onClose();                                 // one click selects and closes; the selected tile just closes
  }, [selectedUrl, onSelect, onClose]);

  if (!isOpen) return null;

  const count = pool.length;
  const sub = subtitle ?? (count === 1 ? '1 item' : `${count} items`);
  const canAdd = !!(onAddPexels || onAddUpload);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const header = (
    <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Inter Tight', Inter, sans-serif", fontWeight: 700, fontSize: 17, letterSpacing: '-.01em', color: 'var(--text)', lineHeight: 1.2 }}>
          {title}
        </div>
        <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>{sub} · click to select · hold and drag to reorder</span>
      </div>
      {canAdd && (
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="tend-btn tend-btn--icon tend-btn--icon-sm"
          style={showAdd ? { background: 'var(--accent-blue)', borderColor: 'transparent' } : undefined}
          title="Add media"
          aria-label="Add media"
          aria-expanded={showAdd}
          data-testid="gallery-add"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
      <button type="button" onClick={onClose} className="tend-btn tend-btn--icon tend-btn--icon-sm" aria-label="Close" title="Close" data-testid="gallery-close">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );

  const body = (
    <div className="tend-scroll flex flex-col gap-3" style={{ overflowY: 'auto', minHeight: 0 }}>
      {/* The pop-down: the shared Pexels / Upload picker. It fills its
          positioned parent, so the panel must not shrink. */}
      {showAdd && (
        <div className="relative h-[380px] shrink-0 rounded-xl overflow-hidden border border-white/15" data-testid="gallery-add-panel">
          <PexelsMediaPicker
            onSelectMedia={(media) => { onAddPexels?.(media); setShowAdd(false); }}
            onLocalUpload={() => { setShowAdd(false); onAddUpload?.(); }}
            onClose={() => setShowAdd(false)}
            isUploading={isUploading}
            instanceId={instanceId}
            allowedType={allowedType}
          />
        </div>
      )}

      {count === 0 ? (
        <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4, padding: '24px 0', textAlign: 'center' }}>
          No media yet — add one with +
        </span>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={pool} strategy={rectSortingStrategy}>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))' }} data-testid="gallery-grid">
              {pool.map((url, i) => (
                <SortableTile
                  key={url}
                  url={url}
                  number={i + 1}
                  selected={url === selectedUrl}
                  hero={!!heroUrl && url === heroUrl}
                  resolveUrl={resolveUrl}
                  onSelect={() => select(url)}
                  onDelete={onDelete ? () => onDelete(url) : undefined}
                  deleteTitle={deleteTitle}
                  aspect={tileAspect}
                  fit={tileFit}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );

  const panelStyle: React.CSSProperties = { borderRadius: 20, padding: PAD, display: 'flex', flexDirection: 'column' };

  if (!wide) {
    // Bottom sheet (the feedback panel's recipe): a veil that closes on tap,
    // the panel hugging the bottom.
    return createPortal(
      <div
        role="presentation"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: panelZ, background: 'rgba(255,255,255,.04)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 12px calc(12px + env(safe-area-inset-bottom))' }}
        data-testid="gallery-sheet-veil"
      >
        <div
          ref={panelRef}
          className="tend-surface tend-f2 tend-modal-card"
          style={{ ...panelStyle, width: '100%', maxWidth: 480, maxHeight: '70vh' }}
          onClick={stop} onMouseDown={stop} onTouchStart={stop}
          data-testid="media-gallery"
          data-layout="sheet"
        >
          {header}
          {body}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      className="tend-surface tend-f2 tend-modal-card"
      style={{ ...panelStyle, position: 'fixed', left: pos?.left ?? GAP, top: pos?.top ?? GAP, width: pos?.width ?? PANEL_MAX_WIDTH,
        height: pos?.height ?? '85vh', zIndex: panelZ, visibility: pos ? 'visible' : 'hidden' }}
      onClick={stop} onMouseDown={stop} onTouchStart={stop}
      data-testid="media-gallery"
      data-layout="floating"
    >
      {header}
      {body}
    </div>,
    document.body,
  );
};

export default MediaGalleryModal;
